import React, { useState } from "react";
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  Plus,
  ArrowUpRight,
  Calendar as CalIcon,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useTasks,
  useSubtasks,
  useCreateTask,
  useCompleteTask,
  useMyAgenda,
  type TaskItem,
} from "@/hooks/useTasks";

const STATUS_ICONS: Record<number, typeof Circle> = {
  0: Circle,
  1: Circle,
  2: Clock,
  3: AlertTriangle,
  4: CheckCircle2,
  5: Circle,
};

const PRIORITY_COLORS: Record<number, string> = {
  0: "text-muted-foreground",
  1: "text-foreground",
  2: "text-amber-500",
  3: "text-red-500",
};

const PRIORITY_LABELS: Record<number, string> = {
  0: "Low",
  1: "Normal",
  2: "High",
  3: "Urgent",
};

const COLUMNS = [
  { status: 1, label: "Todo", color: "border-blue-500/40" },
  { status: 2, label: "In Progress", color: "border-amber-500/40" },
  { status: 3, label: "Blocked", color: "border-red-500/40" },
  { status: 4, label: "Done", color: "border-emerald-500/40" },
];

export default function Tasks() {
  const { data: tasks = [], isLoading } = useTasks();
  const { data: agenda } = useMyAgenda();
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState(1);
  const [newDue, setNewDue] = useState("");

  const handleCreate = () => {
    if (!newTitle.trim()) return;
    createTask.mutate(
      { title: newTitle, priority: newPriority, dueDate: newDue || undefined },
      { onSuccess: () => { setNewTitle(""); setShowNew(false); setNewDue(""); } },
    );
  };

  const topLevelTasks = tasks.filter((t) => !t.parentTaskId);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Tasks</h1>
          <p className="text-sm text-muted-foreground">
            Shared task board — assign, track, and complete work together.
          </p>
        </div>
        <button
          onClick={() => setShowNew(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New Task
        </button>
      </div>

      {agenda && (agenda.overdue.length > 0 || agenda.today.length > 0) && (
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Your Agenda
          </h2>
          <div className="flex flex-wrap gap-4">
            {agenda.overdue.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-red-500">
                <AlertTriangle className="h-4 w-4" />
                <span className="font-medium">{agenda.overdue.length} overdue</span>
              </div>
            )}
            {agenda.today.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-amber-500">
                <CalIcon className="h-4 w-4" />
                <span className="font-medium">{agenda.today.length} due today</span>
              </div>
            )}
            {agenda.inProgress.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-blue-500">
                <ArrowUpRight className="h-4 w-4" />
                <span className="font-medium">{agenda.inProgress.length} in progress</span>
              </div>
            )}
          </div>
        </div>
      )}

      {showNew && (
        <div className="rounded-xl border border-primary/30 bg-card p-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="flex-1">
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="What needs to be done?"
                autoFocus
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
            <select
              value={newPriority}
              onChange={(e) => setNewPriority(Number(e.target.value))}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            >
              <option value={0}>Low</option>
              <option value={1}>Normal</option>
              <option value={2}>High</option>
              <option value={3}>Urgent</option>
            </select>
            <input
              type="date"
              value={newDue}
              onChange={(e) => setNewDue(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
            <button
              onClick={handleCreate}
              disabled={!newTitle.trim() || createTask.isPending}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            >
              Add
            </button>
            <button
              onClick={() => setShowNew(false)}
              className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:text-foreground"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="py-12 text-center text-muted-foreground">Loading tasks...</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          {COLUMNS.map((col) => {
            const colTasks = topLevelTasks.filter((t) => t.status === col.status);
            return (
              <div key={col.status} className={cn("rounded-xl border-t-4 border border-border bg-card/50 p-3", col.color)}>
                <div className="mb-3 flex items-center justify-between">
                  <h3 className="text-sm font-semibold">{col.label}</h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium">
                    {colTasks.length}
                  </span>
                </div>
                <div className="space-y-2">
                  {colTasks.map((task) => (
                    <TaskCard key={task.id} task={task} onComplete={() => completeTask.mutate(task.id)} />
                  ))}
                  {colTasks.length === 0 && (
                    <p className="py-4 text-center text-xs text-muted-foreground">No tasks</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {topLevelTasks.filter((t) => t.status === 0).length > 0 && (
        <div className="rounded-xl border border-border bg-card/50 p-4">
          <h3 className="mb-3 text-sm font-semibold text-muted-foreground">Backlog</h3>
          <div className="space-y-2">
            {topLevelTasks
              .filter((t) => t.status === 0)
              .map((task) => (
                <TaskCard key={task.id} task={task} onComplete={() => completeTask.mutate(task.id)} />
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TaskCard({ task, onComplete }: { task: TaskItem; onComplete: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [showAddSub, setShowAddSub] = useState(false);
  const { data: subtasks = [] } = useSubtasks(expanded ? task.id : null);
  const createTask = useCreateTask();
  const completeTask = useCompleteTask();
  const [newSubTitle, setNewSubTitle] = useState("");

  const Icon = STATUS_ICONS[task.status] ?? Circle;
  const isDone = task.status >= 4;
  const hasSubtasks = subtasks.length > 0;

  const handleAddSubtask = () => {
    if (!newSubTitle.trim()) return;
    createTask.mutate(
      { title: newSubTitle, parentTaskId: task.id },
      { onSuccess: () => setNewSubTitle("") },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleAddSubtask();
    } else if (e.key === "Escape") {
      setShowAddSub(false);
      setNewSubTitle("");
    }
  };

  return (
    <div className={cn("rounded-lg border border-border bg-background transition-colors hover:border-primary/30", isDone && "opacity-60")}>
      <div className="flex items-start gap-2 p-3">
        <button onClick={onComplete} disabled={isDone} className="mt-0.5 shrink-0">
          <Icon className={cn("h-4 w-4", isDone ? "text-emerald-500" : "text-muted-foreground hover:text-primary")} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <button
              onClick={() => setExpanded(!expanded)}
              className="shrink-0 text-muted-foreground hover:text-foreground"
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </button>
            <p className={cn("text-sm font-medium leading-tight", isDone && "line-through")}>
              {task.title}
            </p>
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 pl-5">
            <span className={cn("text-xs font-medium", PRIORITY_COLORS[task.priority])}>
              {PRIORITY_LABELS[task.priority]}
            </span>
            {task.dueDate && (
              <span className="text-xs text-muted-foreground">
                Due {task.dueDate}
              </span>
            )}
            {task.linkedEntityType && (
              <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                {task.linkedEntityType}
              </span>
            )}
            {hasSubtasks && !expanded && (
              <span className="text-xs text-muted-foreground">
                {subtasks.filter((s) => s.status >= 4).length}/{subtasks.length} done
              </span>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border px-3 pb-3 pt-2">
          {subtasks.length > 0 && (
            <div className="mb-2 space-y-1 pl-4">
              {subtasks.map((sub) => {
                const SubIcon = STATUS_ICONS[sub.status] ?? Circle;
                const subDone = sub.status >= 4;
                return (
                  <div key={sub.id} className="flex items-center gap-2 py-0.5">
                    <button onClick={() => completeTask.mutate(sub.id)} disabled={subDone} className="shrink-0">
                      <SubIcon className={cn("h-3.5 w-3.5", subDone ? "text-emerald-500" : "text-muted-foreground hover:text-primary")} />
                    </button>
                    <span className={cn("text-sm", subDone && "line-through text-muted-foreground")}>
                      {sub.title}
                    </span>
                    {sub.dueDate && (
                      <span className="text-xs text-muted-foreground">Due {sub.dueDate}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showAddSub ? (
            <div className="flex items-center gap-2 pl-4">
              <input
                type="text"
                value={newSubTitle}
                onChange={(e) => setNewSubTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add subtask..."
                autoFocus
                className="flex-1 rounded border border-border bg-background px-2 py-1 text-sm focus:border-primary focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={handleAddSubtask}
                disabled={!newSubTitle.trim() || createTask.isPending}
                className="rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
              >
                Add
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddSub(true)}
              className="ml-4 flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              <Plus className="h-3 w-3" /> Add subtask
            </button>
          )}
        </div>
      )}
    </div>
  );
}
